import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import VehicleAttachmentDialog from './VehicleAttachmentDialog';
import {
  getAttachments,
  getAttachment,
  deleteAttachment,
} from '../../../shared/services/attachmentService';
import { ATTACHMENT_GRID_COLUMNS } from '../../constants/vehicleAttachmentsTabConstants';

const VehicleAttachmentsTab = ({ ptsVehicleID }) => {
  const dispatch = useDispatch();
  const canEditVehicle = getFullPermissions('globals', 'Vehicles', 'any').Edit;
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadAttachments = useCallback(async () => {
    if (!ptsVehicleID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAttachments({ id: ptsVehicleID, type: 'VEHICLE' }),
      onSuccess: (data) => setAttachments(data),
    });
  }, [dispatch, ptsVehicleID]);

  const handleAdd = useCallback(() => {
    setEditingAttachment(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingAttachment(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingAttachment(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadAttachments();
  }, [handleFormClose, loadAttachments]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.FileName || 'attachment'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deleteAttachment(row.ptsAttachmentID),
            onSuccess: () => {
              dispatch(notify('Attachment deleted successfully', 'success'));
              loadAttachments();
            },
          });
        },
        'Delete Attachment'
      );
    },
    [dispatch, requestConfirm, loadAttachments]
  );

  const handleDownload = useCallback(
    async (row) => {
      await executeApiAction({
        setLoading,
        dispatch,
        action: async () => {
          const response = await getAttachment(row.ptsAttachmentID);
          const blob = new Blob([response.buffer], { type: response.type });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = row.FileName;
          link.click();

          window.URL.revokeObjectURL(url);
        },
      });
    },
    [dispatch]
  );

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Attachment"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditVehicle}
        />
      </Box>

      <CommonDataGrid
        rows={attachments}
        columns={ATTACHMENT_GRID_COLUMNS}
        getRowId={(row) => row.ptsAttachmentID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Vehicle Attachments',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditVehicle },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditVehicle },
          custom: [
            {
              icon: <GetAppIcon fontSize="small" color="primary" />,
              tooltip: 'Download',
              onClick: handleDownload,
            },
          ],
        }}
      />

      {formOpen && (
        <VehicleAttachmentDialog
          open={formOpen}
          ptsVehicleID={ptsVehicleID}
          attachment={editingAttachment}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default VehicleAttachmentsTab;
